import { useState } from 'react';

import { useAssignWizard } from 'pages/custom_tests/components/assign_wizard/context';
import { FormattedMessage, useIntl } from 'react-intl';
import { Alert, Badge } from 'react-bootstrap';

export const ConfirmStep = () => {

    const { endDate, hasApplicationQuestions, initialAssignedAccessCodeIds, initialAssignedClassroomIds, maxAttempts, modeId,
        proctoring, questionReview, selectedAccessCodes, selectedClassrooms, startDate } = useAssignWizard();
    const { formatMessage } = useIntl();

    const [smsNoticeDismissed, setSmsNoticeDismissed] = useState(false);

    const modeText = modeId === 1
        ? `${formatMessage({ id: 'training.1fce7301', defaultMessage: 'Training' })} ${formatMessage({ id: 'mode.f65b1f2b', defaultMessage: 'Mode' })}`
        : modeId === 3
            ? (formatMessage({ id: 'both_modes.fdac3d2d', defaultMessage: 'Both Modes' }))
            : `${formatMessage({ id: 'testing.a439953c', defaultMessage: 'Testing' })} ${formatMessage({ id: 'mode.f65b1f2b', defaultMessage: 'Mode' })}`;

    const formatDate = (value: string, withYear = true) => {

        if (!value) return '';
        const parsed = new Date(`${value}T00:00:00`);
        return Number.isNaN(parsed.getTime())
            ? value
            : parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'long', ...(withYear ? { year: 'numeric' as const } : {}) });

    };

    // Only the target being ADDED in this pass is confirmed — pre-existing (locked)
    // assignments are preserved as-is and never re-summarized here.
    const newClassrooms = selectedClassrooms.filter(({ Id }) => !initialAssignedClassroomIds.includes(Id));
    const newAccessCodes = selectedAccessCodes.filter(({ Id }) => !initialAssignedAccessCodeIds.includes(Id));

    const targetNames = [
        ...newClassrooms.map(({ Name }) => Name),
        ...newAccessCodes.map(({ Identifier, Name }) => Name || Identifier)
    ].filter(Boolean);

    // Dates are hidden (and not persisted) when the new target is an access code — the
    // code's own expiration governs access.
    const isAccessCodeTarget = newAccessCodes.length > 0;

    // When both dates fall in the same year, the year is shown once on the end date only
    // ('May 20 - June 16, 2026'). Date inputs are ISO strings, so the year is chars 0-3.
    const sameYear = !!startDate && !!endDate && startDate.slice(0, 4) === endDate.slice(0, 4);
    const startText = formatDate(startDate, !sameYear);
    const endText = formatDate(endDate);
    const dateText = startText && endText
        ? `${startText} - ${endText}`
        : startText || endText || (formatMessage({ id: 'no_date_restriction.e941d4c3', defaultMessage: 'No date restriction' }));

    const attemptsText = maxAttempts && Number(maxAttempts) > 0
        ? maxAttempts
        : (formatMessage({ id: 'unlimited.91630800', defaultMessage: 'Unlimited' }));

    const renderRow = (label: string, value: React.ReactNode, divider = false) => (
        <div className={`d-flex align-items-start gap-3 py-2${divider ? ' border-bottom' : ''}`}>
            <span className={'text-muted'} style={{ minWidth: '10rem' }}>
                {label}
            </span>
            <span className={'fw-semibold'}>
                {value}
            </span>
        </div>
    );

    return (
        <div className={'assign-confirm-step d-flex flex-column gap-3'}>

            {targetNames.length === 0 && (
                <div className={'alert alert-warning mb-0 py-2'}>
                    <FormattedMessage defaultMessage="Select a classroom or access code to assign." id="select_a_classroom_or_access_code_to_ass.ddd00e41" />
                </div>
            )}

            {hasApplicationQuestions && !smsNoticeDismissed && (
                <Alert dismissible className={'mb-0 py-2 small'} onClose={() => setSmsNoticeDismissed(true)} variant={'warning'}>
                    <div className={'fw-bold'}>
                        <FormattedMessage defaultMessage="SMS required" id="sms_required.e1197bd8" />
                    </div>
                    <FormattedMessage defaultMessage="This test includes Application questions. Students will need SMS to complete them." id="this_test_includes_application_questions.e4379ccd" />
                </Alert>
            )}

            <div className={'border rounded bg-light px-3 py-2'}>
                {renderRow(
                    formatMessage({ id: 'targets.42d7d147', defaultMessage: 'Targets' }),
                    targetNames.length ? targetNames.join(', ') : '—',
                    true
                )}
                {!isAccessCodeTarget && renderRow(formatMessage({ id: 'dates.f6f73a3f', defaultMessage: 'Dates' }), dateText)}
                {renderRow(formatMessage({ id: 'mode.f65b1f2b', defaultMessage: 'Mode' }), modeText)}
                {renderRow(formatMessage({ id: 'max_attempts.3822990c', defaultMessage: 'Max Attempts' }), attemptsText)}
                <div className={'d-flex align-items-start gap-3 py-2'}>
                    <span className={'text-muted'} style={{ minWidth: '10rem' }}>
                        <FormattedMessage defaultMessage="Features" id="features.a1804c57" />
                    </span>
                    <span className={'d-flex flex-wrap gap-2'}>
                        <Badge bg={proctoring ? 'primary' : 'secondary'}>
                            {`${formatMessage({ id: 'proctoring.4139a0f9', defaultMessage: 'Proctoring' })} ${proctoring ? (formatMessage({ id: 'on.1db5c4b0', defaultMessage: 'On' })) : (formatMessage({ id: 'off.5159727d', defaultMessage: 'Off' }))}`}
                        </Badge>
                        <Badge bg={questionReview ? 'primary' : 'secondary'}>
                            {`${formatMessage({ id: 'question_review.8f63f046', defaultMessage: 'Question Review' })} ${questionReview ? (formatMessage({ id: 'on.1db5c4b0', defaultMessage: 'On' })) : (formatMessage({ id: 'off.5159727d', defaultMessage: 'Off' }))}`}
                        </Badge>
                    </span>
                </div>
            </div>

        </div>
    );

};
